import { useEffect, useRef, useState } from 'react';
import {
  getTask,
  updateTask,
  createTask,
  listSubtasks,
  listTaskComments,
  postTaskComment,
  reactToComment,
  listTaskAttachments,
  uploadTaskAttachment,
  deleteTaskAttachment,
  archiveTask,
  type TaskListItem,
  type TaskComment,
  type TaskAttachment,
  type TaskStatus,
  type TaskPriority,
  type TaskRecurrence,
  type TaskUpdatePayload,
} from '../api/tasks';
import type { TaskProject, TaskUser } from '../api/tasks';
import type { SpaceListItem, MeResponse } from '../api/auth';
import { useFeather } from '../shell/useFeather';
import { linkifyContentEditable } from './linkify';
import { Avatar, SpaceIcon, AssigneePicker, MAX_ATTACH_BYTES, formatSize, fileToBase64 } from './ukoly-shared';
import { confirmDialog, noticeDialog, promptDialog, insertLinkDialog } from '../shell/dialogs';
import { Button } from '../components/arc/button/button';
import { CompactSelect } from '../components/arc/compact-select/compact-select';

const STATUS_OPTIONS: TaskStatus[] = ['todo', 'in_progress', 'waiting', 'blocked', 'review', 'active', 'done', 'cancelled'];
const PRIORITY_OPTIONS: TaskPriority[] = ['low', 'normal', 'high', 'urgent'];
const RECURRENCE_OPTIONS: TaskRecurrence[] = ['none', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
const RECURRENCE_LABEL: Record<TaskRecurrence, string> = {
  none: 'Neopakovat', daily: 'Denně', weekly: 'Týdně', monthly: 'Měsíčně', quarterly: 'Kvartálně', yearly: 'Ročně',
};

const STATUS_LABEL: Record<string, string> = {
  todo: 'Nové', in_progress: 'Rozpracované', waiting: 'Čekající', blocked: 'Blokované',
  review: 'Revize', active: 'Aktivní', done: 'Hotové', cancelled: 'Zrušeno',
};
const PRIORITY_LABEL: Record<string, string> = { low: 'Low', normal: 'Medium', high: 'High', urgent: 'Urgent' };
const REACTION_EMOJIS = ['👍', '❤️', '🎉', '😄', '👀', '✅', '🙏', '🔥'];

function fmtDate(iso: string | null): string {
  if (!iso) return 'dd.mm.rrrr';
  const d = new Date(iso);
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return 'dd.mm.rrrr';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function extFromFilename(fn: string): string {
  const m = /\.([A-Za-z0-9]+)$/.exec(fn || '');
  return m?.[1] ? m[1].toLowerCase() : '';
}
function attachTypeClass(mime: string | null, fn: string): string {
  const ext = extFromFilename(fn);
  if ((mime || '').startsWith('image/')) return 'type-image';
  if (mime === 'application/pdf' || ext === 'pdf') return 'type-pdf';
  if (ext === 'doc' || ext === 'docx') return 'type-doc';
  if (ext === 'xls' || ext === 'xlsx' || ext === 'csv') return 'type-xls';
  if (ext === 'zip' || ext === 'rar') return 'type-zip';
  return '';
}
function attachBadge(mime: string | null, fn: string): string {
  const ext = extFromFilename(fn) || (mime || '').split('/')[1] || 'file';
  return ext.toUpperCase().slice(0, 4);
}
function attachTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('cs-CZ', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function toDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}
function toDateTimeInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function hasTime(iso: string | null): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return d.getHours() !== 0 || d.getMinutes() !== 0;
}

interface Props {
  taskId: number;
  spaces: SpaceListItem[];
  projects: TaskProject[];
  users: TaskUser[];
  me: MeResponse | null;
  onClose: () => void;
  onChanged?: () => void;
  onOpenTask?: (id: number) => void;
}

function userLabel(u: TaskUser): string {
  return [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || `#${u.id}`;
}

export function UkolyDetail({ taskId, spaces, projects, users, me, onClose, onChanged, onOpenTask }: Props) {
  const [task, setTask] = useState<TaskListItem | null>(null);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [subtasks, setSubtasks] = useState<TaskListItem[]>([]);
  const [attachments, setAttachments] = useState<TaskAttachment[]>([]);
  const [newSub, setNewSub] = useState('');
  const [draft, setDraft] = useState('');
  const [showTime, setShowTime] = useState(false);
  const [showMoreFields, setShowMoreFields] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [copied, setCopied] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');
  const [parentTitle, setParentTitle] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reactionPickerFor, setReactionPickerFor] = useState<number | null>(null);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionItems, setMentionItems] = useState<TaskUser[]>([]);
  const [mentionActive, setMentionActive] = useState(0);
  const paneRef = useRef<HTMLElement | null>(null);
  const commentRef = useRef<HTMLDivElement | null>(null);
  const descRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    setTask(null);
    setShowMoreFields(false);
    Promise.all([
      getTask(taskId),
      listTaskComments(taskId).catch(() => []),
      listSubtasks(taskId).catch(() => []),
      listTaskAttachments(taskId).catch(() => []),
    ])
      .then(([t, c, s, a]) => {
        if (cancelled) return;
        setTask(t);
        setTitleDraft(t.title);
        setComments(c);
        setSubtasks(s);
        setAttachments(a);
        if (t.parentTaskId != null) {
          getTask(t.parentTaskId)
            .then((p) => { if (!cancelled) setParentTitle(p.title); })
            .catch(() => { if (!cancelled) setParentTitle(null); });
        } else {
          setParentTitle(null);
        }
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => { cancelled = true; };
  }, [taskId]);

  async function reloadSubtasks() {
    setSubtasks(await listSubtasks(taskId).catch(() => []));
  }
  async function addSubtask() {
    const title = newSub.trim();
    if (!title || !task) return;
    await createTask({ primarySpaceId: task.primarySpaceId, title, parentTaskId: task.id });
    setNewSub('');
    await reloadSubtasks();
    onChanged?.();
  }
  async function toggleSubtask(sub: TaskListItem) {
    await updateTask(sub.id, { status: sub.status === 'done' ? 'todo' : 'done' });
    await reloadSubtasks();
  }
  async function uploadFile(file: File) {
    if (!task) return;
    if (file.size > MAX_ATTACH_BYTES) {
      setError(`Soubor je příliš velký (${formatSize(file.size)}). Maximum je 25 MB.`);
      return;
    }
    try {
      const base64 = await fileToBase64(file);
      await uploadTaskAttachment(task.id, { originalFilename: file.name, contentBase64: base64, mimeType: file.type || null });
      setAttachments(await listTaskAttachments(task.id).catch(() => []));
    } catch (e) {
      setError(String(e));
    }
  }
  async function uploadFiles(files: FileList | File[]) {
    for (const f of Array.from(files)) await uploadFile(f);
  }
  // Soft-archive the task ("Smazat úkol" in prod). Confirms first, never hard-deletes.
  async function archiveCurrent() {
    if (!task) return;
    if (!(await confirmDialog({ title: 'Archivovat úkol?', message: 'Přesune se do archivu, nesmaže se.', confirmLabel: 'Archivovat' }))) return;
    try {
      await archiveTask(task.id);
      onChanged?.();
      onClose();
    } catch (e) {
      setError(String(e));
    }
  }

  useFeather([task?.id ?? 0, comments.length, reactionPickerFor]);

  useEffect(() => {
    if (task) linkifyContentEditable(descRef.current);
  }, [task?.id, task?.descriptionHtml]);

  // Bez tohoto nastavení Chrome v prázdném contentEditable obaluje enter nekonzistentně a víceřádkový popis se po uložení slije do jednoho odstavce.
  useEffect(() => {
    try { document.execCommand('defaultParagraphSeparator', false, 'div'); } catch { /* no-op */ }
  }, []);

  // Drag&drop attachments. Bound at the DOCUMENT level (capture phase), not the
  // pane element: a real OS drop fires on whatever leaf element is under the
  // cursor (a <select>, an <input>, contentEditable…), and relying on the event
  // bubbling up to a specific pane node proved fragile. Document-level capture
  // sees every drag/drop on the page, so while a task detail is open we can
  // always intercept it. preventDefault MUST run UNCONDITIONALLY on dragenter/
  // dragover - that is what enables the drop; a real macOS/Chrome file drag does
  // not expose dataTransfer.types during dragover (payload is protected until
  // drop), so gating preventDefault on a "Files" check silently rejects it.
  useEffect(() => {
    if (!task) return;
    let dragCounter = 0;
    const onDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounter++;
      setDragOver(true);
    };
    const onDragOver = (e: DragEvent) => {
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const onDragLeave = () => {
      dragCounter--;
      if (dragCounter <= 0) { dragCounter = 0; setDragOver(false); }
    };
    const onDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounter = 0;
      setDragOver(false);
      const files = e.dataTransfer?.files;
      if (files && files.length) void uploadFiles(files);
    };
    document.addEventListener('dragenter', onDragEnter, true);
    document.addEventListener('dragover', onDragOver, true);
    document.addEventListener('dragleave', onDragLeave, true);
    document.addEventListener('drop', onDrop, true);
    return () => {
      document.removeEventListener('dragenter', onDragEnter, true);
      document.removeEventListener('dragover', onDragOver, true);
      document.removeEventListener('dragleave', onDragLeave, true);
      document.removeEventListener('drop', onDrop, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id]);

  const meName = me ? [me.firstName, (me as { lastName?: string }).lastName].filter(Boolean).join(' ') || me.email || 'Já' : '';
  const uploaderName = (uid: number | null): string => {
    if (uid == null) return '-';
    const u = users.find((x) => x.id === uid);
    if (u) return userLabel(u);
    if (me && uid === me.userId) return meName;
    return `#${uid}`;
  };
  const avatarFor = (uid: number | null | undefined): string | null =>
    uid == null ? null : users.find((x) => x.id === uid)?.avatarUrl ?? null;
  const space = task ? spaces.find((s) => s.id === task.primarySpaceId) : undefined;
  const creator = task ? users.find((u) => u.id === task.createdByUserId) : undefined;
  const zadavatel = creator ? userLabel(creator) : task?.createdByUserId != null && me && task.createdByUserId === me.userId ? meName : '-';
  const isDone = task?.status === 'done';

  async function copyLink() {
    const url = `${window.location.origin}/ukoly?taskId=${taskId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      void promptDialog({ title: 'Odkaz na úkol', label: 'Zkopíruj odkaz ručně', initial: url, readOnly: true });
    }
  }

  async function submitComment() {
    if (!task) return;
    const el = commentRef.current;
    if (el) linkifyContentEditable(el);
    const html = (el?.innerHTML ?? '').trim();
    const text = (el?.textContent ?? '').trim();
    if (!text) return;
    const mentionUserIds = el ? collectMentionIds(el) : [];
    const c = await postTaskComment(task.id, { bodyHtml: html, mentionUserIds });
    setComments((cs) => [...cs, c]);
    setDraft('');
    setMentionOpen(false);
    if (el) el.innerHTML = '';
  }

  async function toggleReaction(commentId: number, emoji: string) {
    if (!task) return;
    setReactionPickerFor(null);
    try {
      const updated = await reactToComment(task.id, commentId, emoji);
      setComments((cs) => cs.map((c) => (c.id === commentId ? { ...c, reactions: updated.reactions } : c)));
    } catch (e) {
      setError(String(e));
    }
  }

  function caretOffset(el: HTMLElement): number {
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return 0;
    const range = sel.getRangeAt(0).cloneRange();
    range.selectNodeContents(el);
    range.setEnd(sel.getRangeAt(0).endContainer, sel.getRangeAt(0).endOffset);
    return range.toString().length;
  }
  const MENTION_RE = /@([A-Za-zÀ-ž0-9._-]*(?:\s[A-Za-zÀ-ž0-9._-]*)?)$/;
  function currentMentionQuery(el: HTMLElement): string | null {
    const before = (el.innerText || '').slice(0, caretOffset(el));
    const m = before.match(MENTION_RE);
    return m ? (m[1] ?? '') : null;
  }
  function refreshMention(el: HTMLElement) {
    const q = currentMentionQuery(el);
    if (q == null) { setMentionOpen(false); return; }
    const ql = q.toLowerCase();
    const items = users
      .filter((u) => (ql === '' ? true : userLabel(u).toLowerCase().includes(ql) || (u.email ?? '').toLowerCase().includes(ql)))
      .slice(0, 8);
    setMentionItems(items);
    setMentionActive(0);
    setMentionOpen(items.length > 0);
  }
  function insertMentionChip(u: TaskUser) {
    const el = commentRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const before = (el.innerText || '').slice(0, caretOffset(el));
    const m = before.match(MENTION_RE);
    if (m) for (let i = 0; i < m[0].length; i++) document.execCommand('delete');
    const chip = document.createElement('span');
    chip.className = 'asn-mention-chip';
    chip.setAttribute('data-user-id', String(u.id));
    chip.contentEditable = 'false';
    chip.textContent = '@' + userLabel(u);
    const space = document.createTextNode(' ');
    const r2 = sel.getRangeAt(0);
    r2.insertNode(space);
    r2.insertNode(chip);
    r2.setStartAfter(space);
    r2.setEndAfter(space);
    sel.removeAllRanges();
    sel.addRange(r2);
    setMentionOpen(false);
    setDraft(el.textContent ?? '');
  }
  function collectMentionIds(el: HTMLElement): number[] {
    const ids = new Set<number>();
    el.querySelectorAll('.asn-mention-chip[data-user-id]').forEach((c) => {
      const id = Number(c.getAttribute('data-user-id'));
      if (Number.isInteger(id)) ids.add(id);
    });
    return [...ids];
  }
  function fmt(cmd: 'bold' | 'italic' | 'link' | 'mention') {
    const el = commentRef.current;
    if (!el) return;
    el.focus();
    if (cmd === 'link') {
      void insertLinkDialog();
    } else if (cmd === 'mention') {
      document.execCommand('insertText', false, '@');
      refreshMention(el);
    } else {
      document.execCommand(cmd);
    }
  }

  async function applyPatch(patch: TaskUpdatePayload) {
    if (!task) return;
    try {
      const updated = await updateTask(task.id, patch);
      setTask(updated);
      onChanged?.();
    } catch (e) {
      setError(String(e));
    }
  }

  return (
    <>
      <div className="asn-detail-overlay open" onClick={onClose}></div>
      <aside ref={paneRef} className={`asn-detail-pane asn-arc-pane p1-arc-scope open${dragOver ? ' dragging' : ''}`}>
        <div className="asn-detail-toolbar">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className={`asn-detail-complete${isDone ? ' done' : ''}`}
            onClick={() => applyPatch({ status: isDone ? 'todo' : 'done' })}
          >
            <span className="feather-icon" style={{ width: 14, height: 14 }}><i data-feather="check"></i></span>
            {isDone ? 'Hotovo' : 'Označit jako hotové'}
          </Button>
          <button
            type="button"
            className="asn-icon-btn"
            title={copied ? '✓ Zkopírováno' : 'Kopírovat odkaz na úkol'}
            style={copied ? { color: '#3fa34d' } : undefined}
            onClick={copyLink}
          >
            <span className="feather-icon"><i data-feather="link"></i></span>
          </button>
          <button type="button" className="asn-icon-btn" title="Smazat úkol" onClick={() => void archiveCurrent()}>
            <span className="feather-icon"><i data-feather="trash-2"></i></span>
          </button>
          <div className="asn-detail-toolbar-spacer"></div>
          <button type="button" className="asn-icon-btn" title="Zavřít" onClick={onClose}>
            <span className="feather-icon"><i data-feather="x"></i></span>
          </button>
        </div>

        <div className="asn-detail-body">
          {error ? (
            <div className="alert alert-danger">{error}</div>
          ) : !task ? (
            <div className="asn-empty">Načítám…</div>
          ) : (
            <>
              {task.parentTaskId != null && (
                <a
                  className="asn-parent-breadcrumb"
                  role="button"
                  onClick={() => { if (task.parentTaskId != null) onOpenTask?.(task.parentTaskId); }}
                >
                  <span className="feather-icon"><i data-feather="arrow-left"></i></span>
                  <span>Podúkol úkolu: <strong>{parentTitle ?? '…'}</strong></span>
                </a>
              )}
              <textarea
                className="asn-detail-title"
                rows={1}
                value={titleDraft}
                placeholder="Název úkolu"
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={() => {
                  const t = titleDraft.trim();
                  if (t && t !== task.title) void applyPatch({ title: t });
                  else setTitleDraft(task.title);
                }}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
              />

              <div className="asn-detail-grid asn-task-meta">
                {/* Keep the three fields changed most often together at the top.
                    On phones this becomes a compact two-row control panel. */}
                <div className="asn-task-meta-primary" aria-label="Hlavní nastavení úkolu">
                  <div className="asn-task-meta-field asn-task-meta-field-assignee">
                    <div className="label"><span className="feather-icon"><i data-feather="user"></i></span>Přiřazeno</div>
                    <AssigneePicker
                      users={users}
                      value={task.assigneeUserId ?? null}
                      onChange={(id) => applyPatch({ assigneeUserId: id })}
                    />
                  </div>
                  <div className="asn-task-meta-field asn-task-meta-field-due">
                    <div className="label"><span className="feather-icon"><i data-feather="calendar"></i></span>Termín</div>
                    <div className="dt-due-cell">
                      {showTime || hasTime(task.dueAt) ? (
                        <input
                          type="datetime-local"
                          value={toDateTimeInput(task.dueAt)}
                          onChange={(e) => applyPatch({ dueAt: e.target.value ? new Date(e.target.value).toISOString() : null })}
                        />
                      ) : (
                        <input
                          type="date"
                          value={toDateInput(task.dueAt)}
                          onChange={(e) => applyPatch({ dueAt: e.target.value ? new Date(e.target.value + 'T00:00:00').toISOString() : null })}
                        />
                      )}
                      {!(showTime || hasTime(task.dueAt)) && (
                        <button type="button" className="dt-add-time" onClick={() => setShowTime(true)}>+ Čas</button>
                      )}
                    </div>
                  </div>
                  <div className="asn-task-meta-field asn-task-meta-field-category">
                    <div className="label"><span className="feather-icon"><i data-feather="folder"></i></span>Kategorie</div>
                    <CompactSelect className="asn-arc-select" value={String(task.projectId ?? '')} onChange={(next) => void applyPatch({ projectId: next ? Number(next) : null })} ariaLabel="Kategorie" options={[{ value: '', label: 'Bez kategorie' }, ...projects.filter((p) => p.spaceId === task.primarySpaceId).map((p) => ({ value: String(p.id), label: p.name }))]} />
                  </div>
                </div>

                <button
                  type="button"
                  className={`asn-task-meta-more${showMoreFields ? ' open' : ''}`}
                  aria-expanded={showMoreFields}
                  aria-controls={`task-${task.id}-more-fields`}
                  onClick={() => setShowMoreFields((open) => !open)}
                >
                  <span className="asn-task-meta-more-copy">
                    <strong>Další nastavení</strong>
                    <small>{STATUS_LABEL[task.status] ?? task.status} · {PRIORITY_LABEL[task.priority] ?? task.priority}{space ? ` · ${space.name}` : ''}</small>
                  </span>
                  <svg className="asn-task-meta-more-chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9" /></svg>
                </button>

                <div id={`task-${task.id}-more-fields`} className={`asn-task-meta-secondary${showMoreFields ? ' open' : ''}`}>
                  <div className="asn-task-meta-field asn-task-meta-field-status">
                    <div className="label"><span className="feather-icon"><i data-feather="check-circle"></i></span>Status</div>
                    <CompactSelect className="asn-arc-select" value={task.status} onChange={(next) => void applyPatch({ status: next as TaskStatus })} ariaLabel="Status" options={STATUS_OPTIONS.map((s) => ({ value: s, label: STATUS_LABEL[s] ?? s }))} />
                  </div>
                  <div className="asn-task-meta-field asn-task-meta-field-priority">
                    <div className="label"><span className="feather-icon"><i data-feather="flag"></i></span>Priorita</div>
                    <CompactSelect className="asn-arc-select" value={task.priority} onChange={(next) => void applyPatch({ priority: next as TaskPriority })} ariaLabel="Priorita" options={PRIORITY_OPTIONS.map((p) => ({ value: p, label: PRIORITY_LABEL[p] ?? p }))} />
                  </div>
                  <div className="asn-task-meta-field asn-task-meta-field-recurrence">
                    <div className="label"><span className="feather-icon"><i data-feather="repeat"></i></span>Opakovat</div>
                    <CompactSelect className="asn-arc-select" value={task.recurrence ?? 'none'} onChange={(next) => void applyPatch({ recurrence: next as TaskRecurrence })} ariaLabel="Opakovat" options={RECURRENCE_OPTIONS.map((r) => ({ value: r, label: RECURRENCE_LABEL[r] }))} />
                  </div>
                  <div className="asn-task-meta-field asn-task-meta-field-space">
                    <div className="label"><span className="feather-icon"><i data-feather="grid"></i></span>Space</div>
                    <div className="asn-space-field">
                      <SpaceIcon space={space} size={20} />
                      <CompactSelect className="asn-arc-select" value={String(task.primarySpaceId)} onChange={(next) => void applyPatch({ primarySpaceId: Number(next), projectId: null })} ariaLabel="Space" options={[...spaces.map((s) => ({ value: String(s.id), label: s.name })), ...(!spaces.some((s) => s.id === task.primarySpaceId) ? [{ value: String(task.primarySpaceId), label: space?.name ?? `#${task.primarySpaceId}` }] : [])]} />
                    </div>
                  </div>
                  <div className="asn-task-meta-field asn-task-meta-field-creator">
                    <div className="label"><span className="feather-icon"><i data-feather="user-plus"></i></span>Zadavatel</div>
                    <div className="val asn-creator-val">
                      <Avatar name={zadavatel !== '-' ? zadavatel : 'Uživatel'} userId={task.createdByUserId} avatarUrl={avatarFor(task.createdByUserId)} size={20} />
                      <span>{zadavatel}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="asn-detail-desc-label" style={{ marginTop: 18 }}>Přílohy</div>
              <label className="asn-attach-drop" title="Nebo přetáhni soubor kamkoli do panelu">
                <span className="feather-icon"><i data-feather="paperclip"></i></span>
                <span><strong>Přidat přílohu</strong> - klikni nebo přetáhni soubor kamkoli do panelu</span>
                <input
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => { if (e.target.files?.length) void uploadFiles(e.target.files); e.target.value = ''; }}
                />
              </label>
              <div className="asn-attach-list">
                {attachments.map((a) => (
                  <div key={a.id} className="asn-attach-item" style={{ position: 'relative' }}>
                    <a
                      className="asn-attach-item-link"
                      href={`/api/v1/tasks/${task.id}/attachments/${a.id}/download`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1, minWidth: 0, textDecoration: 'none', color: 'inherit' }}
                    >
                      <div className={`asn-attach-icon ${attachTypeClass(a.mimeType, a.originalFilename)}`}>
                        {attachBadge(a.mimeType, a.originalFilename)}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="asn-attach-name">{a.originalFilename}</div>
                        <div className="asn-attach-meta">
                          {formatSize(a.sizeBytes)} · {uploaderName(a.uploadedByUserId)} · {attachTime(a.createdAt)}
                        </div>
                      </div>
                    </a>
                    <button
                      type="button"
                      className="asn-attach-remove"
                      title="Smazat přílohu"
                      onClick={async (e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!(await confirmDialog({ title: 'Smazat přílohu?', message: `„${a.originalFilename}“ se z úkolu odstraní.`, confirmLabel: 'Smazat', danger: true }))) return;
                        try {
                          await deleteTaskAttachment(task.id, a.id);
                          setAttachments((cur) => cur.filter((x) => x.id !== a.id));
                        } catch { void noticeDialog({ title: 'Smazání se nezdařilo', message: 'Přílohu se nepodařilo smazat, zkus to prosím znovu.' }); }
                      }}
                      style={{
                        position: 'absolute', top: 4, right: 4, width: 20, height: 20,
                        border: 0, borderRadius: '50%', background: 'rgba(0,0,0,0.06)',
                        color: '#6b7280', cursor: 'pointer', fontSize: 14, lineHeight: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                      }}
                    >×</button>
                  </div>
                ))}
              </div>

              <div className="asn-detail-desc-label">Popis</div>
              <div className="asn-desc-wrap">
                <div
                  key={task.id}
                  ref={descRef}
                  className="asn-detail-desc"
                  contentEditable
                  suppressContentEditableWarning
                  dangerouslySetInnerHTML={{ __html: task.descriptionHtml ?? '' }}
                  onPaste={(e) => {
                    // Let the browser insert the pasted text, then shorten URLs.
                    const el = e.currentTarget;
                    setTimeout(() => linkifyContentEditable(el), 0);
                  }}
                  onBlur={(e) => {
                    linkifyContentEditable(e.currentTarget);
                    const html = e.currentTarget.innerHTML.trim();
                    if (html !== (task.descriptionHtml ?? '').trim()) {
                      void applyPatch({ descriptionHtml: html || null });
                    }
                  }}
                />
                <div className="asn-detail-desc-toolbar">
                  <button type="button" title="Tučně (Cmd+B)" onMouseDown={(e) => { e.preventDefault(); document.execCommand('bold'); }}><strong>B</strong></button>
                  <button type="button" title="Kurzíva (Cmd+I)" onMouseDown={(e) => { e.preventDefault(); document.execCommand('italic'); }}><em>I</em></button>
                  <button type="button" title="Odkaz (Cmd+K)" onMouseDown={(e) => { e.preventDefault(); void insertLinkDialog(); }}><span className="feather-icon"><i data-feather="link"></i></span></button>
                </div>
              </div>

              <div className="asn-detail-section-title">
                Podúkoly{' '}
                {subtasks.length > 0 && (
                  <span style={{ color: 'var(--bs-secondary-color)', fontWeight: 400 }}>
                    {subtasks.filter((s) => s.status === 'done').length}/{subtasks.length}
                  </span>
                )}
              </div>
              <div className="asn-subtasks-list">
                {subtasks.map((s) => (
                  <div key={s.id} className={`asn-sub-item${s.status === 'done' ? ' done' : ''}`}>
                    <span
                      className={`asn-sub-check${s.status === 'done' ? ' checked' : ''}`}
                      onClick={() => toggleSubtask(s)}
                    ></span>
                    <span
                      className="asn-sub-title"
                      role="button"
                      style={{ cursor: 'pointer' }}
                      title="Otevřít podúkol"
                      onClick={() => onOpenTask?.(s.id)}
                    >
                      {s.title}
                    </span>
                  </div>
                ))}
                <input
                  className="asn-subtask-add-input"
                  placeholder="+ Přidat podúkol (Enter)"
                  value={newSub}
                  onChange={(e) => setNewSub(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') void addSubtask(); }}
                />
              </div>

              <div className="asn-detail-section-title">
                Komentáře{' '}
                {comments.length > 0 && (
                  <span style={{ color: 'var(--bs-secondary-color)', fontWeight: 400 }}>{comments.length}</span>
                )}
              </div>
              <div className="asn-comments">
                {comments.length === 0 ? (
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>Žádné komentáře.</div>
                ) : (
                  comments.map((c) => (
                    <div key={c.id} className="asn-comment">
                      <Avatar name={c.authorUserId != null ? uploaderName(c.authorUserId) : 'Uživatel'} userId={c.authorUserId} avatarUrl={avatarFor(c.authorUserId)} size={30} />
                      <div className="asn-comment-body">
                        <div className="asn-comment-head">
                          <span className="asn-comment-author">{c.authorUserId != null ? uploaderName(c.authorUserId) : 'Uživatel'}</span>
                          <span className="asn-comment-time">{fmtDateTime(c.createdAt)}</span>
                        </div>
                        <div className="asn-comment-content" dangerouslySetInnerHTML={{ __html: c.bodyHtml }} />
                        <div className="asn-comment-reactions">
                          {(c.reactions ?? []).map((rx) => (
                            <button
                              key={rx.emoji}
                              type="button"
                              className={`asn-reaction${rx.me ? ' mine' : ''}`}
                              title={rx.me ? 'Zrušit reakci' : 'Reagovat'}
                              onClick={() => void toggleReaction(c.id, rx.emoji)}
                            >
                              <span>{rx.emoji}</span>
                              <span className="asn-reaction-count">{rx.count}</span>
                            </button>
                          ))}
                          <div className="asn-reaction-add-wrap">
                            <button
                              type="button"
                              className="asn-reaction-add"
                              title="Přidat reakci"
                              onClick={() => setReactionPickerFor((cur) => (cur === c.id ? null : c.id))}
                            >
                              <span className="feather-icon"><i data-feather="smile"></i></span>
                            </button>
                            {reactionPickerFor === c.id && (
                              <div className="asn-reaction-picker">
                                {REACTION_EMOJIS.map((em) => (
                                  <button key={em} type="button" onClick={() => void toggleReaction(c.id, em)}>{em}</button>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="asn-detail-activity">
                <div className="asn-detail-section-title" style={{ marginTop: 0 }}>Aktivita</div>
                <div className="asn-activity-row">
                  <Avatar name={zadavatel !== '-' ? zadavatel : 'Uživatel'} userId={task.createdByUserId} avatarUrl={avatarFor(task.createdByUserId)} size={24} />
                  <div>
                    <strong>{zadavatel !== '-' ? zadavatel : 'Uživatel'}</strong> vytvořil úkol{' '}
                    {task.createdAt && <span className="asn-activity-time">· {fmtDate(task.createdAt)}</span>}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {task && (
          <div className="asn-detail-footer">
            <div className="asn-comment-compose">
              <div className="asn-mention-anchor">
                <div
                  ref={commentRef}
                  className="asn-comment-input"
                  contentEditable
                  suppressContentEditableWarning
                  data-placeholder="Napiš komentář… Pro označení napiš @"
                  onPaste={(e) => { const el = e.currentTarget; setTimeout(() => linkifyContentEditable(el), 0); }}
                  onInput={(e) => { setDraft(e.currentTarget.textContent ?? ''); refreshMention(e.currentTarget); }}
                  onKeyDown={(e) => {
                    if (mentionOpen && mentionItems.length) {
                      if (e.key === 'ArrowDown') { e.preventDefault(); setMentionActive((i) => Math.min(i + 1, mentionItems.length - 1)); return; }
                      if (e.key === 'ArrowUp') { e.preventDefault(); setMentionActive((i) => Math.max(i - 1, 0)); return; }
                      if (e.key === 'Enter') { e.preventDefault(); const u = mentionItems[mentionActive]; if (u) insertMentionChip(u); return; }
                      if (e.key === 'Escape') { e.preventDefault(); setMentionOpen(false); return; }
                    }
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void submitComment(); }
                  }}
                  onBlur={() => { window.setTimeout(() => setMentionOpen(false), 150); }}
                />
                {mentionOpen && mentionItems.length > 0 && (
                  <div className="asn-mention-popup" style={{ left: 12, bottom: 'calc(100% - 4px)' }}>
                    {mentionItems.map((u, i) => (
                      <div
                        key={u.id}
                        className={`asn-mention-item${i === mentionActive ? ' active' : ''}`}
                        onMouseDown={(e) => { e.preventDefault(); insertMentionChip(u); }}
                      >
                        <Avatar name={userLabel(u)} userId={u.id} avatarUrl={u.avatarUrl} size={22} />
                        <span>{userLabel(u)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="asn-comment-toolbar">
                <button type="button" className="asn-comment-format" title="Bold" onMouseDown={(e) => { e.preventDefault(); fmt('bold'); }}><b>B</b></button>
                <button type="button" className="asn-comment-format" title="Italic" onMouseDown={(e) => { e.preventDefault(); fmt('italic'); }}><i>I</i></button>
                <button type="button" className="asn-comment-format" title="Odkaz" onMouseDown={(e) => { e.preventDefault(); fmt('link'); }}><span className="feather-icon" style={{ width: 12, height: 12 }}><i data-feather="link"></i></span></button>
                <button type="button" className="asn-comment-format" title="Označit uživatele (@)" onMouseDown={(e) => { e.preventDefault(); fmt('mention'); }}><b>@</b></button>
                <div style={{ flex: 1 }}></div>
                <Button type="button" variant="primary" size="sm" className="asn-comment-send" onClick={submitComment} disabled={!draft.trim()}>
                  Odeslat
                </Button>
              </div>
            </div>
          </div>
        )}
      </aside>
    </>
  );
}
